import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import CardHeader from "../../components/common/CardHeader";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Payment from "../../services/Payment";


import logger from "../../utils/logger";
function PaymentFinish() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const { applicationId } = useParams();
  const { accessToken, userName } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [canLeave, setCanLeave] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  let navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const handleResize = () => {
    if (window.innerWidth >= 992) {
      setIsSidebarVisible(true);
    } else {
      setIsSidebarVisible(false);
    }
  };

  const verifyPayment = async () => {
    logger.log("verifyPayment - attempt:", retryCount + 1);
    try {
      const response = await Payment.getStatus(applicationId, accessToken);
      if (response) {
        logger.log("Payment status response:", response);
        setPaymentStatus(response.status);
        setLastUpdated(response.updated_at);
        setRetryCount((prevCount) => prevCount + 1);

        if (["COMPLETED", "CANCELED"].includes(response.status)) {
          setCanLeave(true);
        }

        return response.status;
      }
    } catch (error) {
      logger.error("Error verifying payment:", error);
      setRetryCount((prevCount) => prevCount + 1);
    }
    return null;
  };

  const redirectToApplication = () => {
    navigate(`/application/edit/1/${applicationId}`);
  };

  const redirectToApplicationsList = () => {
    navigate(`/application/list`);
  };

  const handleSuccessfulPayment = async () => {
    // try {
    //   await commitStep(applicationId, userName, accessToken);
    //   logger.log("Step committed successfully");
    // } catch (error) {
    //   logger.error("Error committing step:", error);
    // }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    const fetchData = async () => {
      setLoading(true);

      const initialStatus = await verifyPayment();

      if (!["COMPLETED", "CANCELED"].includes(initialStatus)) {
        setPaymentStatus(initialStatus || "PENDING");
      }

      setLoading(false);
    };

    fetchData();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let timeoutId;

    const handlePaymentStatus = async () => {
      switch (paymentStatus) {
        case "CANCELED":
          timeoutId = setTimeout(() => {
            redirectToApplication();
          }, 10000);
          break;

        case "COMPLETED":
          await handleSuccessfulPayment();
          timeoutId = setTimeout(() => {
            redirectToApplication();
          }, 10000);
          break;

        default:
          if (retryCount < 10 && !canLeave && !loading) {
            const interval = Math.min(5000 + retryCount * 2000, 30000);
            timeoutId = setTimeout(() => {
              verifyPayment();
            }, interval);
          } else if (retryCount >= 10) {
            timeoutId = setTimeout(() => {
              redirectToApplicationsList();
            }, 5000);
          }
          break;
      }
    };

    if (paymentStatus && !loading) {
      handlePaymentStatus();
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [paymentStatus, retryCount, loading, canLeave]);

  const getStatusIcon = () => {
    switch (paymentStatus) {
      case "COMPLETED":
        return <i className="fs-1 bi bi-check-circle-fill text-success" />;
      case "CANCELED":
        return <i className="fs-1 bi bi-x-circle-fill text-danger" />;
      case "PENDING":
      case "WAITING_FOR_CONFIRMATION":
      case "NEW":
        return <LoadingSpinner className="fs-1" text="" small={false} />;
      default:
        return <i className="fs-1 bi bi-question-circle-fill text-warning" />;
    }
  };

  const getStatusMessage = () => {
    switch (paymentStatus) {
      case "COMPLETED":
        return {
          title: "Dziękujemy za dokonanie płatności",
          description:
            "Płatność została potwierdzona. Za chwilę przeniesiemy Cię do listy Twoich wniosków.",
        };
      case "CANCELED":
        return {
          title: "Płatność anulowana",
          description:
            "Płatność została odrzucona. Za chwilę przeniesiemy Cię na stronę, na której możesz ponowić płatność.",
        };
      case "PENDING":
      case "WAITING_FOR_CONFIRMATION":
      case "NEW":
        return {
          title: `Trwa weryfikacja płatności (próba ${retryCount} z 10)`,
          description: canLeave
            ? "Weryfikacja płatności przebiega w tle. Możesz bezpiecznie opuścić tę stronę - zostaniesz powiadomiony o wyniku."
            : "Proszę czekać na weryfikację stanu płatności.",
        };
      default:
        return {
          title: "Płatność nie została zweryfikowana",
          description:
            retryCount >= 10
              ? "Nie udało się zweryfikować statusu płatności. Weryfikacja będzie kontynuowana w tle systemu. Za chwilę przeniesiemy Cię do listy Twoich wniosków."
              : "Trwa weryfikacja płatności w tle systemu.",
        };
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="w-100 overflow-auto">
      <CardHeader text="Płatność zakończona" onlyText="true" />
      {loading ? (
        <LoadingSpinner text={"Trwa weryfikacja płatności."} />
      ) : (
        <div className="card container-fluid shadow ms-4 px-4 py-3 w55">
          <div className="d-flex flex-column">
            <div className="w-100 d-flex flex-row align-items-center">
              <div>{getStatusIcon()}</div>
              <div className="ms-1">
                <strong className="fs-4">{statusMessage.title}</strong>
                <br />
                <span>{statusMessage.description}</span>
                {lastUpdated && (
                  <div className="mt-2 text-muted small">
                    Ostatnia aktualizacja:{" "}
                    {new Date(lastUpdated).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
            {/* 
              {canLeave && (
                <div className="alert alert-info mt-3">
                  <i className="bi bi-info-circle"></i>
                  <strong> Możesz opuścić tę stronę</strong>
                  <br />
                  Weryfikacja płatności zostanie zakończona automatycznie w tle
                  systemu.
                </div>
              )} */}

            <div className="mt-3 d-flex gap-2">
              {paymentStatus === "CANCELED" && (
                <button
                  className="btn btn-primary"
                  onClick={redirectToApplication}
                >
                  Powróć do wniosku
                </button>
              )}
              {paymentStatus === "COMPLETED" && (
                <button
                  className="btn btn-primary"
                  onClick={redirectToApplicationsList}
                >
                  Przejdź do listy wniosków
                </button>
              )}
              {!["COMPLETED", "CANCELED"].includes(paymentStatus) &&
                canLeave && (
                  <button
                    className="btn btn-secondary"
                    onClick={redirectToApplicationsList}
                  >
                    Przejdź do listy wniosków
                  </button>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentFinish;


